import { tr } from './i18n.js';

// Plafond DUR d'images extraites côté client, aligné sur MAX_FRAME_COUNT du
// Worker (validation /api/analyze). Le nombre réellement demandé vient de
// ANALYZE_FRAME_COUNT (src/utils/constants.js, 24 par défaut) ; ce plafond évite
// d'envoyer un payload hors borne si l'appelant demande davantage.
const MAX_EXTRACT_FRAMES = 36;
const DESKTOP_FRAME_PROFILE = Object.freeze({
  aiSize: 640,
  aiQuality: 0.75,
  previewWidth: 480,
  previewHeight: 270,
  previewQuality: 0.64,
  seekTimeoutMs: 2500
});
const MOBILE_FRAME_PROFILE = Object.freeze({
  aiSize: 512,
  aiQuality: 0.70,
  previewWidth: 360,
  previewHeight: 203,
  previewQuality: 0.56,
  seekTimeoutMs: 6000
});

export async function compressVideo(file) {
  if (!window.VideoEncoder) {
    return file;
  }
  return file;
}

export function resolveFrameEncodingProfile(userAgent = '', coarsePointer = false) {
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(String(userAgent || '')) || coarsePointer === true;
  return isMobile ? MOBILE_FRAME_PROFILE : DESKTOP_FRAME_PROFILE;
}

function currentFrameEncodingProfile() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches === true;
  return resolveFrameEncodingProfile(ua, coarse);
}

// Calcule le rectangle de destination « letterbox » pour dessiner une source
// (vidéo/image) DANS un canvas cible en PRÉSERVANT son aspect ratio : la source
// est centrée et mise à l'échelle pour tenir entièrement, sans étirement. Le
// reste du canvas (bandes haut/bas ou gauche/droite) constitue le padding neutre.
// Exporté pour être testable sans DOM.
export function computeLetterboxRect(srcW, srcH, dstW, dstH) {
  const sw = Number(srcW) > 0 ? Number(srcW) : dstW;
  const sh = Number(srcH) > 0 ? Number(srcH) : dstH;
  const scale = Math.min(dstW / sw, dstH / sh);
  const w = Math.round(sw * scale);
  const h = Math.round(sh * scale);
  const x = Math.round((dstW - w) / 2);
  const y = Math.round((dstH - h) / 2);
  return { x, y, width: w, height: h };
}

// Dessine `video` dans `ctx` (canvas dstW×dstH) sans déformation : padding neutre
// gris moyen (#808080) — volontairement distinct du terrain (vert) et des maillots
// (clair/foncé) pour ne pas tromper l'analyse — puis l'image letterboxée par-dessus.
function drawLetterboxed(ctx, video, dstW, dstH) {
  const srcW = video.videoWidth || dstW;
  const srcH = video.videoHeight || dstH;
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, dstW, dstH);
  const r = computeLetterboxRect(srcW, srcH, dstW, dstH);
  ctx.drawImage(video, r.x, r.y, r.width, r.height);
}

// Calcule les instants (secondes) auxquels échantillonner `count` frames sur la
// fenêtre [start, end], BORNES INCLUSES. Contrairement à l'ancienne formule
// (t = start + step*i + 0.1) qui s'arrêtait avant `end` et pouvait donc manquer un
// but situé en toute fin d'action, on répartit ici les frames jusqu'à la borne de
// fin : t[0] proche de `start` (avec une marge min pour éviter une frame noire/keyframe
// pile sur la borne) et t[n-1] proche de `end` (safe max `end - 0.01`).
// Le nombre d'images est borné comme extractKeyFrames (1..MAX_EXTRACT_FRAMES).
// Fonction pure exportée pour être testable sans navigateur.
export function computeFrameSampleTimes(start, end, count) {
  const s = Number.isFinite(start) ? Math.max(0, start) : 0;
  const e = Number.isFinite(end) && end > s ? end : s + 0.1;
  // Dernier instant atteignable sans toucher la toute fin du segment (seek fiable).
  const maxSeek = Math.max(s, e - 0.01);
  const n = Math.max(1, Math.min(Math.floor(count) || 1, MAX_EXTRACT_FRAMES));
  // Marge min appliquée à la première frame pour ne pas capturer pile sur la borne
  // de départ (souvent une frame noire / keyframe), sans franchir le voisin ni maxSeek.
  const MARGIN = 0.1;
  const round = (t) => Math.round(t * 100) / 100;

  if (n === 1) {
    return [round(Math.min(maxSeek, s + Math.min(MARGIN, (maxSeek - s) / 2)))];
  }

  const times = [];
  for (let i = 0; i < n; i++) {
    times.push(s + ((maxSeek - s) * i) / (n - 1));
  }
  // Décale la 1re frame de la marge min sans dépasser la 2e ni maxSeek.
  const margin = Math.min(MARGIN, (times[1] - times[0]) / 2);
  times[0] = Math.min(maxSeek, times[0] + margin);
  return times.map(round);
}

// Marge de CONFIRMATION DE FIN D'ACTION (secondes). Quand la vidéo dure plus
// longtemps que la fin du segment choisi, on étend LÉGÈREMENT la fenêtre
// d'échantillonnage des frames au-delà de segment.end. But : si l'utilisateur
// coupe pile au moment d'un but, la toute dernière frame du segment peut tomber
// juste AVANT le ballon dans le but ; cette marge permet à la dernière image
// échantillonnée de montrer le ballon au fond / la célébration immédiate.
// Ce n'est PAS une analyse libre hors clip : on ne dépasse JAMAIS la durée
// réelle de la vidéo et on reste borné à cette petite marge. Le segment original
// (affiché dans l'UI et envoyé à l'analyse) est inchangé ; seuls les instants
// d'échantillonnage peuvent dépasser légèrement segment.end.
export const END_CONFIRMATION_MARGIN_S = 1.0;

// Fin EFFECTIVE d'échantillonnage : segment.end + marge de confirmation de fin
// d'action, sans jamais dépasser la durée réelle de la vidéo. Si la vidéo ne
// dure pas plus que segment.end, la fin reste segment.end (aucun dépassement).
// Fonction pure exportée pour être testable sans navigateur.
export function computeSamplingEnd(segmentEnd, fullDuration, margin = END_CONFIRMATION_MARGIN_S) {
  const e = Number.isFinite(segmentEnd) ? segmentEnd : 0;
  const d = Number.isFinite(fullDuration) && fullDuration > 0 ? fullDuration : e;
  const m = Number.isFinite(margin) && margin > 0 ? margin : 0;
  return Math.min(d, e + m);
}

// Lit la durée d'un fichier vidéo (en secondes) sans le charger entièrement.
export function getVideoDuration(videoFile) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      URL.revokeObjectURL(url);
      resolve(d);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(tr('Erreur chargement vidéo', 'Video loading error')));
    };
    video.src = url;
    video.load();
  });
}

/**
 * Extrait des frames clés réparties dans une fenêtre temporelle donnée.
 * Seul l'intervalle [segment.start, segment.end] est échantillonné : les frames
 * envoyées à l'analyse proviennent donc uniquement du segment choisi.
 *
 * Renvoie des métadonnées pour que la page débrief affiche EXACTEMENT les frames
 * envoyées à l'IA (pas une recapture fragile depuis la vidéo) :
 *   - frames        : payload base64 JPEG 512x512 pour l'IA, l'image source 16:9
 *                     étant letterboxée (aspect ratio préservé, padding neutre)
 *                     pour ne plus déformer le terrain ni les positions ;
 *   - frame_times   : instant exact (secondes) réellement capturé pour chaque frame ;
 *   - frame_previews: data URLs JPEG 16:9 légères (480x270) à persister/afficher.
 *
 * Anti-doublon : une signature basse résolution compare chaque frame à la
 * précédente ; si elles sont quasi identiques, quelques instants alternatifs plus
 * loin dans le pas courant sont tentés pour éviter les Avant/Après identiques.
 *
 * @param {File|Blob} videoFile
 * @param {number} count
 * @param {{start?: number, end?: number}} segment
 * @returns {Promise<{frames: string[], frame_times: number[], frame_previews: string[], timeline_frames: Array}>}
 */
export async function extractKeyFrames(videoFile, count = 8, segment = null) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      const fullDuration = Number.isFinite(video.duration) ? video.duration : 0;
      if (!fullDuration) {
        URL.revokeObjectURL(url);
        reject(new Error(tr('Durée vidéo invalide', 'Invalid video duration')));
        return;
      }
      // Fenêtre par défaut : 15 premières secondes (comportement historique).
      let start = 0;
      let end = Math.min(fullDuration, 15);
      if (segment && Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end > segment.start) {
        start = Math.max(0, segment.start);
        end = Math.min(fullDuration, segment.end);
      }
      const windowDuration = Math.max(0.1, end - start);

      const frames = [];
      const frameTimes = [];
      const framePreviews = [];

      const profile = currentFrameEncodingProfile();
      // Canvas IA + canvas aperçu léger. Sur mobile, on garde 24 frames mais on
      // réduit le poids de chaque image : c'est le payload total qui fragilise
      // Safari/Chrome mobile, pas le nombre d'instants tactiques.
      // L'image source (souvent 16:9) est letterboxée DANS ce carré : on préserve
      // l'aspect ratio (padding neutre) au lieu de l'étirer, sinon le terrain et les
      // positions sont déformés et l'IA inverse plus facilement les rôles.
      const aiCanvas = document.createElement('canvas');
      aiCanvas.width = profile.aiSize;
      aiCanvas.height = profile.aiSize;
      const aiCtx = aiCanvas.getContext('2d');
      const previewCanvas = document.createElement('canvas');
      previewCanvas.width = profile.previewWidth;
      previewCanvas.height = profile.previewHeight;
      const previewCtx = previewCanvas.getContext('2d');
      // Canvas signature basse résolution pour détecter les doublons visuels.
      const sigCanvas = document.createElement('canvas');
      sigCanvas.width = 16;
      sigCanvas.height = 16;
      const sigCtx = sigCanvas.getContext('2d');

      const seekTo = (time) => new Promise((r, rej) => {
        const timeout = setTimeout(() => {
          video.removeEventListener('seeked', handler);
          rej(new Error('Timeout extraction frame'));
        }, profile.seekTimeoutMs);
        const handler = () => {
          clearTimeout(timeout);
          video.removeEventListener('seeked', handler);
          r();
        };
        video.addEventListener('seeked', handler);
        video.currentTime = time;
      });

      // Signature : 16x16 en luminance → 256 valeurs comparables d'une frame à l'autre.
      const computeSignature = () => {
        sigCtx.drawImage(video, 0, 0, 16, 16);
        const { data } = sigCtx.getImageData(0, 0, 16, 16);
        const sig = new Array(256);
        for (let i = 0; i < 256; i++) {
          const o = i * 4;
          sig[i] = data[o] * 0.299 + data[o + 1] * 0.587 + data[o + 2] * 0.114;
        }
        return sig;
      };

      // Différence moyenne absolue entre deux signatures (0 = identiques).
      const signatureDiff = (a, b) => {
        if (!a || !b) return Infinity;
        let sum = 0;
        for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
        return sum / a.length;
      };

      // En dessous de ce seuil, deux frames sont jugées quasi identiques.
      const DUP_THRESHOLD = 6;

      const frameCount = Math.max(1, Math.min(count, MAX_EXTRACT_FRAMES));
      // Fin effective d'échantillonnage : on s'autorise une petite marge de
      // CONFIRMATION DE FIN D'ACTION au-delà de segment.end si la vidéo dure plus
      // longtemps. Le segment original (start/end ci-dessus, persisté + envoyé à
      // l'analyse) reste inchangé : seuls les instants des frames peuvent dépasser
      // légèrement end, pour ne pas manquer un but capté juste après la coupe.
      const sampleEnd = computeSamplingEnd(end, fullDuration);
      // Répartition des frames sur [start, sampleEnd] bornes incluses : la fin (but
      // en toute fin d'action) est désormais échantillonnée, y compris la marge.
      const sampleTimes = computeFrameSampleTimes(start, sampleEnd, frameCount);
      const step = windowDuration / frameCount; // conservé pour les sauts anti-doublon

      (async () => {
        let prevSig = null;
        for (let i = 0; i < frameCount; i++) {
          const baseTime = sampleTimes[i];
          let chosenTime = baseTime;
          await seekTo(chosenTime);
          let sig = computeSignature();
          // Anti-doublon : si trop proche de la frame précédente, tente quelques
          // instants alternatifs un peu plus loin dans le pas courant.
          let attempts = 0;
          while (prevSig && signatureDiff(sig, prevSig) < DUP_THRESHOLD && attempts < 3) {
            attempts++;
            const altTime = Math.min(sampleEnd - 0.01, baseTime + (step * attempts) / 4);
            if (altTime <= chosenTime + 0.01) break; // plus de marge dans la fenêtre
            chosenTime = altTime;
            await seekTo(chosenTime);
            sig = computeSignature();
          }
          drawLetterboxed(aiCtx, video, profile.aiSize, profile.aiSize);
          previewCtx.drawImage(video, 0, 0, profile.previewWidth, profile.previewHeight);
          const aiFrame = aiCanvas.toDataURL('image/jpeg', profile.aiQuality).split(',')[1];
          const preview = previewCanvas.toDataURL('image/jpeg', profile.previewQuality);
          frames.push(aiFrame);
          framePreviews.push(preview);
          frameTimes.push(Math.round(chosenTime * 100) / 100);
          prevSig = sig;
        }
        URL.revokeObjectURL(url);
        resolve({
          frames,
          frame_times: frameTimes,
          frame_previews: framePreviews,
          timeline_frames: frameTimes.map((time, index) => ({
            index,
            time,
            preview: framePreviews[index]
          }))
        });
      })().catch((e) => {
        URL.revokeObjectURL(url);
        reject(e);
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(tr('Erreur chargement vidéo', 'Video loading error')));
    };

    video.load();
  });
}

/**
 * Capture des images à des instants précis depuis une URL vidéo (CORS requis).
 * Utilisé pour reconstruire les comparaisons d'images clés sur la page détail.
 * @param {string} videoUrl
 * @param {number[]} times — instants en secondes
 * @returns {Promise<string[]>} dataURLs JPEG
 */
export function captureFramesAt(videoUrl, times) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const canvas = document.createElement('canvas');
      canvas.width = 480;
      canvas.height = 270;
      const ctx = canvas.getContext('2d');

      const captureAt = (time) => new Promise((r, rej) => {
        const safeTime = duration ? Math.min(Math.max(0, time), Math.max(0, duration - 0.05)) : Math.max(0, time);
        const timeout = setTimeout(() => {
          video.removeEventListener('seeked', handler);
          rej(new Error('Timeout capture frame'));
        }, 4000);
        const handler = () => {
          clearTimeout(timeout);
          video.removeEventListener('seeked', handler);
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            r(canvas.toDataURL('image/jpeg', 0.7));
          } catch (e) {
            rej(e);
          }
        };
        video.addEventListener('seeked', handler);
        video.currentTime = safeTime;
      });

      (async () => {
        const out = [];
        for (const t of times) out.push(await captureAt(t));
        resolve(out);
      })().catch(reject);
    };

    video.onerror = () => reject(new Error(tr('Erreur chargement vidéo', 'Video loading error')));
    video.src = videoUrl;
    video.load();
  });
}
